import {
  AccessControlRequestStatus,
  AclVerb,
} from '@agilelab/plugin-wb-access-control-common';
import { AccessControlRequestNotification } from '@agilelab/plugin-wb-notification-common';
import {
  customAlertApiRef,
  ProgressButton,
  useUsername,
  useUserProfile,
  WbAvatar,
  WbButton,
  WbLink,
} from '@agilelab/plugin-wb-platform';
import {
  anyCaseToCapitalCase,
  CustomError,
} from '@agilelab/plugin-wb-platform-common';
import { Entity } from '@mainstage/catalog-model';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import { Chip, IconButton, Typography, useTheme } from '@material-ui/core';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useId, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { JsonObject } from 'type-fest';
import { notificationApiRef } from '../../notificationApiRef';
import { AccessControlDisplayFields } from './AccessControlDisplayFields';
import { userCache } from './cache';
import { MarkAsReadButton } from './MarkAsReadButton';
import {
  NotificationDisclosure,
  NotificationDisclosureAction,
  NotificationDisclosureDetails,
} from './NotificationDisclosure';
import { useNotificationStyle } from './NotificationsPageStyle';
import { useNotification } from './useNotificationProvider';
import { useProductPendingRequestsLink } from '@agilelab/plugin-wb-access-control';

const parseRequesterName = (
  requesterName: string,
): { kind: string; name: string } => {
  const splittedRequesterName = requesterName.split(':');

  return { kind: splittedRequesterName[0], name: splittedRequesterName[1] };
};

const composeMessages = (
  verb: AclVerb,
): { deliverySuccess: string; deliveryFailure: string } => {
  if (verb === AclVerb.Grant) {
    return {
      deliverySuccess:
        'Access request accepted! A follow-up message will inform you when the access is effective into the system.',
      deliveryFailure: 'Failed to grant access',
    };
  }

  return {
    deliverySuccess:
      'Revoke request accepted! A follow-up message will inform you when the revoke is effective into the system.',
    deliveryFailure: 'Failed to revoke access',
  };
};

export const AccessControlRequestNotificationDisclosure = (props: {
  verb: AclVerb;
  notification: AccessControlRequestNotification;
  onAccordionExpansionsChange: (value: string | boolean) => void;
  expandedAccordion: string | boolean;
}) => {
  const { verb, notification, onAccordionExpansionsChange, expandedAccordion } =
    props;
  const classes = useNotificationStyle();
  const catalogApi = useApi(catalogApiRef);
  const theme = useTheme();
  const notificationCatalog = useApi(notificationApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const { profile } = useUserProfile();
  const loggedUsername = useUsername();
  const [loading, setLoading] = useState('');
  const queryClient = useQueryClient();
  const { setRead } = useNotification();
  const detailsRegionId = useId();

  const { value: askerEntity } = useAsync(async (): Promise<
    Entity | undefined
  > => {
    if (userCache.has(notification.notification_request.requester))
      return userCache.get(notification.notification_request.requester)!;
    const userEntity = await catalogApi.getEntityByRef({
      name: parseRequesterName(notification.notification_request.requester)
        .name,
      namespace: 'default',
      kind: 'user',
    });
    if (userEntity) {
      userCache.set(notification.notification_request.requester, userEntity);
    }
    return userEntity;
  }, [notification]);

  const isExpanded = expandedAccordion === notification.id.toString();

  const onToggle = useCallback(() => {
    onAccordionExpansionsChange(
      isExpanded ? false : notification.id.toString(),
    );
  }, [isExpanded, notification.id, onAccordionExpansionsChange]);

  const updateAccessControlRequestResponse = useCallback(
    async (status: AccessControlRequestStatus) => {
      setLoading(status);
      try {
        await notificationCatalog
          .updateNotificationResponse(
            notification.id.toString(),
            {
              authorized_by: loggedUsername?.username || profile.email,
              authorized_by_display_name:
                loggedUsername?.displayName || profile.displayName,
              status: status,
              timestamp: new Date().toJSON(),
            },
            await identityApi.getCredentials(),
          )
          .then(
            _ => {
              if (status === AccessControlRequestStatus.ACCEPT) {
                alertApi.post({
                  message: composeMessages(verb).deliverySuccess,
                  severity: 'success',
                });
              }
              queryClient.invalidateQueries({
                queryKey: ['paginatedByRecipient'],
              });
              setRead(notification.id.toString());
              setLoading('');
            },
            error => {
              alertApi.post({
                error,
                severity: 'error',
              });
              setLoading('');
            },
          );
      } catch (error) {
        alertApi.post({
          error: new CustomError(
            composeMessages(verb).deliveryFailure,
            error.message,
          ),
          severity: 'error',
          timeoutMillis: 5000,
        });
        setLoading('');
      }
    },
    [
      alertApi,
      identityApi,
      loggedUsername?.displayName,
      loggedUsername?.username,
      notification.id,
      notificationCatalog,
      profile.displayName,
      profile.email,
      queryClient,
      setRead,
      verb,
    ],
  );

  const pendingRequestsLink = useProductPendingRequestsLink(
    notification.notification_request.id_dataproduct_instance,
  );

  const notificationHeader = (
    <div className={classes.expandableSummaryHeaderOutsideDisclosure}>
      <Typography className={classes.notificationTime}>
        {DateTime.fromISO(notification.created_at).toFormat('dd/MM/yyyy HH:mm')}
      </Typography>
      <div className={classes.expandableSummaryHeaderActions}>
        <MarkAsReadButton notification={notification} />
        <IconButton
          onClick={onToggle}
          size="small"
          aria-expanded={isExpanded}
          aria-controls={detailsRegionId}
          aria-label="toggle notification details"
          className={`${classes.disclosureToggleButton} ${
            isExpanded ? classes.disclosureToggleButtonExpanded : ''
          }`}
        >
          <ExpandMoreIcon color="primary" />
        </IconButton>
      </div>
    </div>
  );

  const productName =
    notification.notification_request.dataproduct.display_name ||
    notification.notification_request.dataproduct.name;

  return (
    <div className={classes.expandableDisclosureContainer}>
      {notificationHeader}
      <NotificationDisclosure
        expanded={isExpanded}
        style={{
          border: 'none',
          borderBottom: 'none',
          backgroundColor: !!notification.read_at
            ? 'transparent'
            : theme.palette.common.white,
        }}
      >
        <div className={classes.expandableSummaryBody}>
          <div className={classes.expandableSummaryBodyContent}>
            <div className={classes.summaryDescription}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {!notification.notification_response && (
                  <ErrorOutlineOutlinedIcon fontSize="small" color="primary" />
                )}
                {notification.notification_response?.status ===
                  AccessControlRequestStatus.ACCEPT && (
                  <CheckCircleOutlineOutlinedIcon
                    fontSize="small"
                    style={{ color: theme.palette.success.main }}
                  />
                )}
                {notification.notification_response?.status ===
                  AccessControlRequestStatus.REJECT && (
                  <NotInterestedIcon
                    fontSize="small"
                    style={{ color: theme.palette.error.main }}
                  />
                )}
                <WbAvatar
                  displayName={
                    notification.notification_request.requester_display_name
                  }
                  picture={
                    (askerEntity?.spec?.profile as JsonObject | undefined)
                      ?.picture as string
                  }
                  classes={{ avatar: classes.avatar }}
                />
              </div>
              <div className={classes.summaryDescriptionText}>
                <b>
                  {notification.notification_request.requester_display_name}
                </b>{' '}
                <Typography variant="subtitle2" paragraph>
                  {verb === AclVerb.Grant
                    ? 'requested access to a resource'
                    : 'requested a revoke for a resource'}
                </Typography>
                <span>
                  <b>name:</b>{' '}
                  <WbLink
                    to={`/marketplace/search/${notification.notification_request.id_dataproduct_instance}`}
                  >
                    <Typography color="primary" component="b">
                      {productName}
                    </Typography>
                  </WbLink>
                </span>
                <span>
                  <b>domain:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.dataproduct.domain}
                  </Typography>
                </span>
                <span>
                  <b>environment:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.environment}
                  </Typography>
                </span>
                <span>
                  <b>version:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.dataproduct.version}
                  </Typography>
                </span>
              </div>
            </div>
          </div>
        </div>
        <NotificationDisclosureDetails id={detailsRegionId}>
          <div>
            <div className={classes.contentElementHeader}>
              <Typography
                variant="h6"
                color="primary"
                style={{ marginBottom: theme.spacing(1) }}
                paragraph
              >
                Consumable Interface
              </Typography>
              <Typography variant="body1">
                <b>Name:</b> {notification.notification_request.outputport.name}
              </Typography>
              <Typography variant="body1">
                <b>Type:</b>{' '}
                {anyCaseToCapitalCase(
                  notification.notification_request.outputport.output_port_type,
                  true,
                )}
              </Typography>
            </div>
            <div className={classes.contentElementHeader}>
              <Typography
                variant="h6"
                color="primary"
                style={{ marginBottom: theme.spacing(1) }}
                paragraph
              >
                Requested for
              </Typography>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: theme.spacing(1),
                }}
              >
                {notification.notification_request.identities &&
                  notification.notification_request.identities.map(i => (
                    <Chip title={i} key={i} label={i} size="small" />
                  ))}
              </div>
            </div>
            <AccessControlDisplayFields
              notification={notification.notification_request}
            />
            {notification.notification_response?.status ===
              AccessControlRequestStatus.ACCEPT && (
              <Typography style={{ color: theme.palette.success.main }}>
                Request accepted on:{' '}
                {DateTime.fromISO(
                  notification.notification_response?.timestamp,
                ).toFormat('dd/MM/yyyy HH:mm')}
              </Typography>
            )}
            {notification.notification_response?.status ===
              AccessControlRequestStatus.REJECT && (
              <Typography style={{ color: theme.palette.error.main }}>
                Access rejected :{' '}
                {DateTime.fromISO(
                  notification.notification_response?.timestamp,
                ).toFormat('dd/MM/yyyy HH:mm')}
              </Typography>
            )}
          </div>
        </NotificationDisclosureDetails>
        {!notification.notification_response && (
          <NotificationDisclosureAction>
            <>
              <WbButton
                style={{ marginRight: theme.spacing(1) }}
                variant="outlined"
                color="primary"
                label="Manage Requests"
                to={pendingRequestsLink}
              />
              <ProgressButton
                meshColor="red"
                variant="contained"
                inProgress={loading === AccessControlRequestStatus.REJECT}
                disabled={loading === AccessControlRequestStatus.ACCEPT}
                onClick={() =>
                  updateAccessControlRequestResponse(
                    AccessControlRequestStatus.REJECT,
                  )
                }
              >
                Reject
              </ProgressButton>
              <ProgressButton
                meshColor="green"
                variant="contained"
                inProgress={loading === AccessControlRequestStatus.ACCEPT}
                disabled={loading === AccessControlRequestStatus.REJECT}
                onClick={() =>
                  updateAccessControlRequestResponse(
                    AccessControlRequestStatus.ACCEPT,
                  )
                }
              >
                Accept
              </ProgressButton>
            </>
          </NotificationDisclosureAction>
        )}
      </NotificationDisclosure>
    </div>
  );
};
